/**
 * ExpenseCorrectionModal — M-EC-5 Expense-Storno Frontend
 *
 * Guided assistant that turns a booked expense into a signed, negated
 * correction document ("Ausgabe stornieren/korrigieren",
 * architecture-expense-storno.md §6.3). The ORIGINAL expense is never
 * written — only a new `expenses` document is created via
 * `api/expensesApi.ts::createCorrectionExpense` (GoBD immutability).
 *
 * Two variants:
 *   - `storno`         — Voll-Storno / Generalumkehr (the receipt does not
 *                          belong in the books at all).
 *   - `privatentnahme` — Umbuchung auf Privatentnahme (private cause) with
 *                          automatic input-VAT reversal (§ 15 Abs. 1a UStG).
 *
 * Key design invariants (architecture §6.3/§12/§18):
 *   - Reason (Begründung) is mandatory, non-empty (Belegprinzip).
 *   - Variant B requires an explicit confirmation checkbox before booking is
 *     possible — the reversal only applies for PRIVATE cause, never for
 *     business use (§ 12 Nr. 1 EStG, § 15 Abs. 1a UStG).
 *   - A permanent warning next to variant B clarifies that a *business*
 *     Bewirtung keeps the VAT deduction (§ 15 Abs. 1a S. 2 UStG) — this
 *     prevents a wrong VAT-storno for a plain category mistake.
 *   - Correction date defaults to today; a soft (non-blocking) warning is
 *     shown when it falls into an already-closed period.
 *   - Advisors (`useIsReadOnly`) never see the booking actions — defense in
 *     depth, mirrors `ExpenseBookingModal` (M11b-3). The hard authorization
 *     boundary stays server-side (Firestore Rules / PHP), this is UX only.
 *   - After a successful booking, the Steuerberater is notified
 *     (non-blocking — `notifyAdvisorOfExpenseCorrection` never throws).
 *
 * @module ExpenseCorrectionModal
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IconClose, IconWarning, IconInfo } from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { Expense } from '../../types/expenseTypes';
import { MessageAuthorRole } from '../../types/messagingTypes';
import { createCorrectionExpense } from '../../api/expensesApi';
import { notifyAdvisorOfExpenseCorrection } from '../../utils/expenseCorrectionNotify';
import { DATEV_PRIVATENTNAHME_ACCOUNTS, SkrKontenrahmen } from '../../utils/datevMapping';
import { centsToEuros } from '../../utils/moneyUtils';
import logger from '../../utils/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

type CorrectionType = 'storno' | 'privatentnahme';

interface ExpenseCorrectionModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** The booked expense to correct (must be `status==='booked'`, not itself a correction). */
  expense: Expense;
  /** Called when the modal is dismissed without booking. */
  onClose: () => void;
  /**
   * Called after a successful correction booking with the new Firestore
   * document ID. The parent should reload the expense list.
   */
  onCorrected: (correctionId: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Formats integer cents as a EUR currency string (de-DE locale). */
function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

/** Today's date as ISO YYYY-MM-DD. */
function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Soft-check whether an ISO date falls into an already-closed period
 * (any month before the current calendar month). Non-blocking — only
 * drives a warning banner, never prevents booking (architecture §6.3.3).
 */
function isPastPeriod(dateISO: string): boolean {
  if (!dateISO || dateISO.length < 7) return false;
  const currentPeriod = getTodayISO().slice(0, 7);
  return dateISO.slice(0, 7) < currentPeriod;
}

// ─── ExpenseCorrectionModal ────────────────────────────────────────────────────

/**
 * Guided correction assistant for a single booked expense. Renders nothing
 * when `open` is `false`.
 */
const ExpenseCorrectionModal: React.FC<ExpenseCorrectionModalProps> = ({
  open,
  expense,
  onClose,
  onCorrected,
}) => {
  const { t } = useTranslation('expenses');
  const { currentUser, sellerData, activeRole, effectiveScopeUid } = useAuth();
  // Defense-in-depth (M11b-3 pattern) — the hard boundary is server-side.
  const isReadOnly = useIsReadOnly();

  const [correctionType, setCorrectionType] = useState<CorrectionType>('storno');
  const [reason, setReason] = useState('');
  const [correctionDate, setCorrectionDate] = useState(getTodayISO());
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset local state whenever the modal is (re-)opened for an expense.
  useEffect(() => {
    if (open) {
      setCorrectionType('storno');
      setReason('');
      setCorrectionDate(getTodayISO());
      setConfirmChecked(false);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expense.id]);

  const handleVariantChange = useCallback((variant: CorrectionType) => {
    setCorrectionType(variant);
    // The confirmation checkbox is only meaningful for variant B.
    if (variant === 'storno') setConfirmChecked(false);
  }, []);

  const skr: SkrKontenrahmen = (sellerData?.datevSkr as SkrKontenrahmen | undefined) ?? 'SKR03';

  // ── Derived preview (read-only, always safe to compute) ─────────────────
  const vatReversedPreview = expense.vatDeductibleCents !== 0;
  const gegenkontoLabel =
    correctionType === 'privatentnahme'
      ? DATEV_PRIVATENTNAHME_ACCOUNTS[skr]
      : t('correction.modal.previewCreditorAccount');
  const kontoLabel =
    expense.skrAccount && expense.skrAccount.trim().length > 0
      ? expense.skrAccount
      : t('correction.modal.previewDefaultAccount');
  const previewAmount = formatCents(Math.abs(expense.grossCents));

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length > 0;
  const dateValid = correctionDate.trim().length > 0;
  const checkboxValid = correctionType !== 'privatentnahme' || confirmChecked;
  const canSubmit = reasonValid && dateValid && checkboxValid && !submitting;

  const showPeriodWarning = dateValid && isPastPeriod(correctionDate);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (submitting || !canSubmit) return;

    setSubmitting(true);
    try {
      const correctionId = await createCorrectionExpense(expense, {
        correctionType,
        reason: trimmedReason,
        correctionDate,
        skr,
      });
      toast.success(t('correction.modal.success'));

      // Non-blocking StB-notify — never throws, failure is only logged.
      if (currentUser) {
        const authorRole: MessageAuthorRole = activeRole === 'advisor' ? 'advisor' : 'owner';
        await notifyAdvisorOfExpenseCorrection(
          {
            original: expense,
            correctionType,
            vatReversed: vatReversedPreview,
            reason: trimmedReason,
            correctionExpenseId: correctionId,
          },
          { scopeUid: effectiveScopeUid, authorUid: currentUser.uid, authorRole },
        );
      }

      onCorrected(correctionId);
    } catch (err) {
      logger.error('ExpenseCorrectionModal: createCorrectionExpense failed', err);
      toast.error(t('correction.modal.errorSave'));
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    canSubmit,
    expense,
    correctionType,
    trimmedReason,
    correctionDate,
    skr,
    currentUser,
    activeRole,
    effectiveScopeUid,
    vatReversedPreview,
    t,
    onCorrected,
  ]);

  if (!open) return null;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expense-correction-modal-title"
    >
      <div
        className="w-full max-w-lg bg-surface-raised border border-hairline rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 id="expense-correction-modal-title" className="text-primary font-semibold text-base">
            {t('correction.modal.title')}
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-muted hover:text-primary transition-colors disabled:opacity-40 p-1 -mr-1"
            aria-label={t('correction.modal.cancel')}
          >
            <IconClose />
          </button>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Original summary */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <p className="text-faint text-xs mb-0.5">{t('correction.modal.originalLabel')}</p>
              <p className="text-primary font-medium">{expense.supplierName || '—'}</p>
            </div>
            <div>
              <p className="text-faint text-xs mb-0.5">{t('modal.gross')}</p>
              <p className="text-primary font-mono text-xs">{formatCents(expense.grossCents)}</p>
            </div>
            <div>
              <p className="text-faint text-xs mb-0.5">{t('modal.invoiceDate')}</p>
              <p className="text-primary text-xs">{expense.expenseDate || '—'}</p>
            </div>
          </div>

          <hr className="border-hairline" />

          {/* Variant selector */}
          <div>
            <p className="text-xs text-muted mb-2">{t('correction.modal.variantLabel')}</p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline rounded-lg px-3 py-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="correctionType"
                  value="storno"
                  checked={correctionType === 'storno'}
                  onChange={() => handleVariantChange('storno')}
                  disabled={submitting}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-primary text-sm font-medium">
                    {t('correction.modal.variantStorno')}
                  </span>
                  <span className="block text-muted text-xs mt-0.5">
                    {t('correction.modal.variantStornoHint')}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline rounded-lg px-3 py-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="correctionType"
                  value="privatentnahme"
                  checked={correctionType === 'privatentnahme'}
                  onChange={() => handleVariantChange('privatentnahme')}
                  disabled={submitting}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-primary text-sm font-medium">
                    {t('correction.modal.variantPrivatentnahme')}
                  </span>
                  <span className="block text-muted text-xs mt-0.5">
                    {t('correction.modal.variantPrivatentnahmeHint')}
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Variant B: VAT hint + business-entertainment warning + confirmation checkbox */}
          {correctionType === 'privatentnahme' && (
            <>
              <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5 text-[var(--tone-info-fg)] text-xs">
                <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('correction.modal.vatHintPrivatentnahme')}</span>
              </div>
              <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg px-3 py-2.5 text-[var(--tone-warning-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('correction.modal.businessEntertainmentWarning')}</span>
              </div>
              <label className="flex items-start gap-2 text-xs text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  disabled={submitting}
                  className="mt-0.5"
                />
                <span>{t('correction.modal.confirmCheckboxLabel')}</span>
              </label>
            </>
          )}

          <hr className="border-hairline" />

          {/* Reason (mandatory) */}
          <div>
            <label className="block text-xs text-muted mb-1" htmlFor="correctionReason">
              {t('correction.modal.reasonLabel')}
            </label>
            <textarea
              id="correctionReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder={t('correction.modal.reasonPlaceholder')}
              className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint resize-none"
            />
            {!reasonValid && reason.length > 0 && (
              <p className="text-[var(--tone-danger-fg)] text-xs mt-1">{t('correction.modal.reasonRequired')}</p>
            )}
          </div>

          {/* Correction date */}
          <div>
            <label className="block text-xs text-muted mb-1" htmlFor="correctionDate">
              {t('correction.modal.dateLabel')}
            </label>
            <input
              id="correctionDate"
              type="date"
              value={correctionDate}
              onChange={(e) => setCorrectionDate(e.target.value)}
              disabled={submitting}
              className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50"
            />
            {showPeriodWarning && (
              <div className="flex items-start gap-2 mt-2 bg-[var(--tone-caution-bg)] border border-[var(--tone-caution-border)] rounded-lg px-3 py-2.5 text-[var(--tone-caution-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('correction.modal.datePastPeriodWarning')}</span>
              </div>
            )}
          </div>

          <hr className="border-hairline" />

          {/* Resulting counter-booking preview (read-only) */}
          <div>
            <p className="text-muted text-xs font-medium uppercase tracking-wide mb-2">
              {t('correction.modal.previewTitle')}
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">{t('correction.modal.previewAccount')}</span>
                <span className="text-primary font-mono text-xs">{kontoLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">{t('correction.modal.previewCounterAccount')}</span>
                <span className="text-primary font-mono text-xs">{gegenkontoLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">{t('correction.modal.previewVatReversal')}</span>
                <span className={`font-mono text-xs ${vatReversedPreview ? 'text-[var(--tone-success-fg)]' : 'text-faint'}`}>
                  {vatReversedPreview
                    ? t('correction.modal.previewVatYes')
                    : t('correction.modal.previewVatNo')}
                </span>
              </div>
              <div className="flex justify-between font-semibold border-t border-hairline pt-1.5">
                <span className="text-secondary">{t('modal.gross')}</span>
                <span className="text-primary font-mono text-xs">{previewAmount}</span>
              </div>
            </div>
          </div>

          {/* Jurist-Auflage A2 — StB-Verantwortung/Nicht-Steuerberatungs-Hinweis.
              Statisch sichtbar bei BEIDEN Varianten (kein Variant-B-Gate), dezent
              unter der Gegenbuchungs-Vorschau (jurist-review-expense-storno.md §A2). */}
          <div className="flex items-start gap-2 text-faint text-xs">
            <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
            <span>{t('correction.modal.advisorResponsibilityNote')}</span>
          </div>
        </div>

        {/* ── Footer / actions ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-hairline bg-[rgb(var(--surface-base-rgb)/0.4)]">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-secondary hover:text-primary transition-colors disabled:opacity-40"
          >
            {t('correction.modal.cancel')}
          </button>
          {/* M11b-3: advisor (read-only) never sees the booking action. */}
          {!isReadOnly && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-5 py-2 text-sm rounded-lg bg-red-700 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? t('correction.modal.submitting') : t('correction.modal.submit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseCorrectionModal;
